// File: app/src/App.jsx

import { useState, useEffect } from 'react';
import { getCurrentUser, signOut, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import Layout from './components/layout/Layout';
import Analytics from './components/Analytics';
import Home from './pages/Home';
import Ask from './pages/Ask';
import Private from './pages/Private';
import About from './pages/About';
import Contact from './pages/Contact';
import Latest from './pages/Latest';
import NotFound from './pages/NotFound';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ResourcesReferences from './pages/ResourcesReferences';
import CookieConsentBanner from './components/CookieConsentBanner';
import { initGA, trackPageView } from './utils/analytics';
import { shouldBypassConsent, setCrawlerConsent } from './utils/crawlerDetection';
import { 
  initializeConsent, 
  grantAllConsent, 
  grantEssentialConsent, 
  isAnalyticsEnabled,
  getStoredConsent 
} from './utils/consentManager';

const PrivateRoute = ({ user, children }) => {
  if (!user) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState(false);
  const [currentConsentState, setCurrentConsentState] = useState(null);

  useEffect(() => {
    checkAuthState();
    checkCookieConsent();
  }, []);

  const checkCookieConsent = () => {
    // Check if this is a crawler first
    if (shouldBypassConsent()) {
      setCrawlerConsent();
      setHasAnalyticsConsent(true);
      setShowCookieConsent(false);
      return;
    }
    
    // Initialize consent state using the new consent manager
    const consentState = initializeConsent();
    setCurrentConsentState(consentState);
    
    // Check if user has already given consent
    const storedConsent = getStoredConsent();
    if (!storedConsent) {
      // No consent given yet, show banner
      setShowCookieConsent(true);
    } else {
      // User has given consent before, check analytics status
      const analyticsEnabled = isAnalyticsEnabled(storedConsent);
      setHasAnalyticsConsent(analyticsEnabled);
      
      if (analyticsEnabled) {
        // Initialize GA if analytics consent was granted
        initGA(true);
        // Track initial page view
        trackPageView(window.location.pathname + window.location.search);
      }
    }
  };

  const handleAcceptAll = () => {
    const newConsentState = grantAllConsent();
    setCurrentConsentState(newConsentState);
    setHasAnalyticsConsent(true);
    setShowCookieConsent(false);
    
    // Initialize Google Analytics
    initGA(true);
    // Track initial page view
    trackPageView(window.location.pathname + window.location.search);
  };

  const handleEssentialOnly = () => {
    const newConsentState = grantEssentialConsent();
    setCurrentConsentState(newConsentState);
    setHasAnalyticsConsent(false);
    setShowCookieConsent(false);
    
    // Do not initialize Google Analytics
    console.log('User chose essential cookies only - Analytics disabled');
  };

  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      
      // Get tokens
      const session = await fetchAuthSession();
      setTokens({
        idToken: session.tokens?.idToken?.toString(),
        accessToken: session.tokens?.accessToken?.toString(),
        refreshToken: session.tokens?.refreshToken?.toString()
      });
      
      setError(null);
    } catch (err) {
      setUser(null);
      setTokens(null);
      // Don't set error for unauthenticated state
      if (err.name !== 'UserUnAuthenticatedException') {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await signInWithRedirect();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setTokens(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Encountering error... {error}</div>;
  }

  return (
    <BrowserRouter>
      <Analytics />
      <Routes>
        <Route path="/" element={<Layout user={user} onSignIn={handleSignIn} onSignOut={handleSignOut} />}>
          <Route index element={<Home />} />
          <Route path="ask" element={<Ask />} />
          <Route
            path="private"
            element={
              <PrivateRoute user={user}>
                <Private />
              </PrivateRoute>
            }
          />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="latest" element={<Latest />} />
          <Route path="terms" element={<TermsOfService />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="resources" element={<ResourcesReferences />} />  
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      {showCookieConsent && (
        <CookieConsentBanner 
          onAcceptAll={handleAcceptAll}
          onEssentialOnly={handleEssentialOnly}
          onDismiss={() => setShowCookieConsent(false)}
        />
      )}
    </BrowserRouter>
  );
}

export default App;